/**
 * usePlatformAnalytics Hook
 *
 * Custom React hook for managing platform analytics data.
 * Provides data fetching, state management, and actions for admin analytics dashboard.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-147 - Platform Analytics & Reporting (34 SP)
 *
 * Features:
 * - Platform overview metrics (users, experiences, bookings, revenue, reviews)
 * - Guide performance metrics with filtering and pagination
 * - Top performers lists (revenue, bookings, recently active)
 * - Manual analytics refresh
 * - Last refresh timestamp
 *
 * Usage:
 * ```jsx
 * const {
 *   overview,
 *   guidePerformance,
 *   topRevenue,
 *   topBookings,
 *   recentlyActive,
 *   lastRefresh,
 *   loading,
 *   error,
 *   refreshAnalytics,
 *   fetchGuidePerformance,
 *   fetchGuidePerformanceById
 * } = usePlatformAnalytics();
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';

const API_BASE = '/api/v1/admin/analytics';

/**
 * Hook for managing platform analytics data
 */
export function usePlatformAnalytics(autoFetch = true) {
  // State for overview metrics
  const [overview, setOverview] = useState(null);

  // State for guide performance
  const [guidePerformance, setGuidePerformance] = useState({
    guides: [],
    pagination: {}
  });

  // State for top performers
  const [topRevenue, setTopRevenue] = useState([]);
  const [topBookings, setTopBookings] = useState([]);
  const [recentlyActive, setRecentlyActive] = useState([]);

  // State for refresh timestamp
  const [lastRefresh, setLastRefresh] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  /**
   * Fetch platform overview metrics
   */
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/overview`, {
        method: 'GET'
      });

      if (response.success) {
        setOverview(response.data);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch overview';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch guide performance metrics with filtering and pagination
   *
   * @param {Object} filters - Filter options
   * @param {string} filters.search - Search by business name or email
   * @param {string} filters.status - Filter by approval status
   * @param {string} filters.sortBy - Sort field (default: total_revenue)
   * @param {string} filters.sortOrder - Sort order (ASC/DESC, default: DESC)
   * @param {number} filters.limit - Results per page (default: 50)
   * @param {number} filters.offset - Pagination offset (default: 0)
   */
  const fetchGuidePerformance = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.search) {params.append('search', filters.search);}
      if (filters.status) {params.append('status', filters.status);}
      if (filters.sortBy) {params.append('sortBy', filters.sortBy);}
      if (filters.sortOrder) {params.append('sortOrder', filters.sortOrder);}
      if (filters.limit) {params.append('limit', filters.limit.toString());}
      if (filters.offset) {params.append('offset', filters.offset.toString());}

      const response = await apiRequest(`${API_BASE}/guides?${params.toString()}`, {
        method: 'GET'
      });

      if (response.success) {
        setGuidePerformance({
          guides: response.data,
          pagination: response.pagination
        });
      }

      return { success: true, data: response.data, pagination: response.pagination };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch guide performance';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch specific guide performance by ID
   *
   * @param {string} guideId - Guide ID
   */
  const fetchGuidePerformanceById = useCallback(async (guideId) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/guides/${guideId}`, {
        method: 'GET'
      });

      if (response.success) {
        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to fetch guide performance' };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch guide performance';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setActionLoading(false);
    }
  }, []);

  /**
   * Fetch top guides by revenue
   *
   * @param {number} limit - Number of guides to return (default: 10)
   */
  const fetchTopRevenue = useCallback(async (limit = 10) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/top-revenue?limit=${limit}`, {
        method: 'GET'
      });

      if (response.success) {
        setTopRevenue(response.data);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch top revenue guides';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch top guides by booking count
   *
   * @param {number} limit - Number of guides to return (default: 10)
   */
  const fetchTopBookings = useCallback(async (limit = 10) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/top-bookings?limit=${limit}`, {
        method: 'GET'
      });

      if (response.success) {
        setTopBookings(response.data);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch top booking guides';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch recently active guides
   *
   * @param {number} limit - Number of guides to return (default: 10)
   */
  const fetchRecentlyActive = useCallback(async (limit = 10) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/recently-active?limit=${limit}`, {
        method: 'GET'
      });

      if (response.success) {
        setRecentlyActive(response.data);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch recently active guides';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch last refresh timestamp
   */
  const fetchLastRefresh = useCallback(async () => {
    try {
      const response = await apiRequest(`${API_BASE}/last-refresh`, {
        method: 'GET'
      });

      if (response.success) {
        setLastRefresh(response.data.last_updated);
      }

      return { success: true, data: response.data.last_updated };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch last refresh';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Fetch all dashboard data
   * Convenience method to load all analytics data at once
   */
  const fetchAllData = useCallback(async () => {
    await Promise.all([
      fetchOverview(),
      fetchGuidePerformance(),
      fetchTopRevenue(),
      fetchTopBookings(),
      fetchRecentlyActive(),
      fetchLastRefresh()
    ]);
  }, [fetchOverview, fetchGuidePerformance, fetchTopRevenue, fetchTopBookings, fetchRecentlyActive, fetchLastRefresh]);

  /**
   * Manually refresh analytics materialized views
   * Triggers database refresh and cache invalidation
   */
  const refreshAnalytics = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/refresh`, {
        method: 'POST'
      });

      if (response.success) {
        // After refresh, fetch last refresh timestamp
        await fetchLastRefresh();

        return { success: true, data: response.data };
      }

      return { success: false, error: 'Failed to refresh analytics' };
    } catch (err) {
      const errorMessage = err.message || 'Failed to refresh analytics';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setActionLoading(false);
    }
  }, [fetchLastRefresh]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchAllData();
    }
  }, [autoFetch, fetchAllData]);

  return {
    // State
    overview,
    guidePerformance,
    topRevenue,
    topBookings,
    recentlyActive,
    lastRefresh,
    loading,
    error,
    actionLoading,

    // Actions
    fetchOverview,
    fetchGuidePerformance,
    fetchGuidePerformanceById,
    fetchTopRevenue,
    fetchTopBookings,
    fetchRecentlyActive,
    refreshAnalytics,
    fetchLastRefresh,
    fetchAllData
  };
}

export default usePlatformAnalytics;
